import { gql } from '@apollo/client';
import { IMAGE_FIELDS, MEDIA_FIELDS, DELIVERY_UNIT_FIELDS } from '../fragments/common';
import { MERCHANT_DELIVERY_FIELDS, WAREHOUSE_DELIVERY_FIELDS } from '../fragments/contract';
import { ENGRAVING_FIELDS } from '../fragments/engraving';
import { PIECE_FIELDS, PIECE_REFERENCE_FIELDS } from '../fragments/piece';
import { MONEY_FIELDS } from '../fragments/common';
import { ACCESSORY_FIELDS, ACCESSORY_REFERENCE_FIELDS } from './accessory';
import { PARTY_FIELDS } from './party';
import { MERCHANT_CHANNEL_FIELDS } from './merchant';
import { FONT_FIELDS } from './font';
import { SYMBOL_COLLECTION_FIELDS, SYMBOL_FIELDS } from './symbol';
// --------------------------------------------------------- Platform Merchant Order ---------------------------------------------------------
const PLATFORM_MERCHANT_ORDER_HISTORY_ITEM_FIELDS = gql`
  fragment PlatformMerchantOrderHistoryItemFields on PlatformMerchantOrderHistoryItem {
    createdAt
    state
    note
  }
`;

const PLATFORM_MERCHANT_PIECE_ORDER_ITEM_FIELDS = gql`
  ${ENGRAVING_FIELDS}
  ${PIECE_REFERENCE_FIELDS}
  ${MONEY_FIELDS}
  fragment PlatformMerchantPieceOrderItemFields on PlatformMerchantPieceOrderItem {
    quantity
    engravings {
      ...EngravingFields
    }
    pieceReference {
      ...PieceReferenceFields
    }
    platformPrice {
      ...MoneyFields
    }
    merchantRetailPrice {
      ...MoneyFields
    }
  }
`;

const PLATFORM_MERCHANT_ACCESSORY_ORDER_ITEM_FIELDS = gql`
  ${ACCESSORY_REFERENCE_FIELDS}
  ${MONEY_FIELDS}
  fragment PlatformMerchantAccessoryOrderItemFields on PlatformMerchantAccessoryOrderItem {
    quantity
    accessoryReference {
      ...AccessoryReferenceFields
    }
    platformPrice {
      ...MoneyFields
    }
    merchantRetailPrice {
      ...MoneyFields
    }
  }
`;

const PLATFORM_MERCHANT_ORDER_ITEM_FIELDS = gql`
  ${PLATFORM_MERCHANT_PIECE_ORDER_ITEM_FIELDS}
  ${PLATFORM_MERCHANT_ACCESSORY_ORDER_ITEM_FIELDS}
  fragment PlatformMerchantOrderItemFields on PlatformMerchantOrderItem {
    id
    merchantCustomerOrderItemId
    pieceOrderItem {
      ...PlatformMerchantPieceOrderItemFields
    }
    accessoryOrderItem {
      ...PlatformMerchantAccessoryOrderItemFields
    }
  }
`;

export const PLATFORM_MERCHANT_ORDER_FIELDS = gql`
  ${PARTY_FIELDS}
  ${PLATFORM_MERCHANT_ORDER_ITEM_FIELDS}
  ${MERCHANT_CHANNEL_FIELDS}
  ${MERCHANT_DELIVERY_FIELDS}
  ${PLATFORM_MERCHANT_ORDER_HISTORY_ITEM_FIELDS}
  fragment PlatformMerchantOrderFields on PlatformMerchantOrder {
    id
    state
    platformSaleCatalogId
    merchantPartyId
    merchantParty {
      ...PartyFields
    }
    merchantChannelId

    merchantChannel {
      ...MerchantChannelFields
    }

    merchantChannelCatalogId
    merchantCustomerOrderId
    merchantDelivery {
      ...MerchantDeliveryFields
    }

    items {
      ...PlatformMerchantOrderItemFields
    }
    history {
      ...PlatformMerchantOrderHistoryItemFields
    }

    createdAt
    updatedAt
  }
`;

// --------------------------------------------------------- Platform Producer Order ---------------------------------------------------------
const PLATFORM_PRODUCER_ORDER_HISTORY_ITEM_FIELDS = gql`
  fragment PlatformProducerOrderHistoryItemFields on PlatformProducerOrderHistoryItem {
    createdAt
    state
    note
  }
`;

const PLATFORM_PRODUCER_ORDER_ITEM_PIECE_FIELDS = gql`
  ${PIECE_REFERENCE_FIELDS}
  ${MONEY_FIELDS}
  fragment PlatformProducerOrderItemPieceFields on PlatformProducerOrderItemPiece {
    id
    quantity
    pieceReference {
      ...PieceReferenceFields
    }
    inventoryReference
  }
`;

const PLATFORM_PRODUCER_ORDER_ITEM_ACCESSORY_FIELDS = gql`
  ${ACCESSORY_REFERENCE_FIELDS}
  ${MONEY_FIELDS}
  fragment PlatformProducerOrderItemAccessoryFields on PlatformProducerOrderItemAccessory {
    id
    quantity
    accessoryReference {
      ...AccessoryReferenceFields
    }
    inventoryReference
  }
`;

const PLATFORM_PRODUCER_ORDER_ITEM_FIELDS = gql`
  ${PLATFORM_PRODUCER_ORDER_ITEM_PIECE_FIELDS}
  ${PLATFORM_PRODUCER_ORDER_ITEM_ACCESSORY_FIELDS}
  ${MONEY_FIELDS}
  fragment PlatformProducerOrderItemFields on PlatformProducerOrderItem {
    id
    platformMerchantOrderItemId
    pieces {
      ...PlatformProducerOrderItemPieceFields
    }
    accessories {
      ...PlatformProducerOrderItemAccessoryFields
    }
    price {
      ...MoneyFields
    }
  }
`;

export const PLATFORM_PRODUCER_ORDER_FIELDS = gql`
  ${PLATFORM_PRODUCER_ORDER_ITEM_FIELDS}
  ${PLATFORM_PRODUCER_ORDER_HISTORY_ITEM_FIELDS}
  ${PARTY_FIELDS}
  ${DELIVERY_UNIT_FIELDS}
  ${MERCHANT_DELIVERY_FIELDS}
  fragment PlatformProducerOrderFields on PlatformProducerOrder {
    id
    state

    producerPartyId
    producerParty {
      ...PartyFields
    }
    producerDeliveryUnitId
    producerDeliveryUnit {
      ...DeliveryUnitFields
    }

    deliveryUnitSaleCatalogId

    platformMerchantOrderId
    delivery {
      ...MerchantDeliveryFields
    }

    items {
      ...PlatformProducerOrderItemFields
    }
    history {
      ...PlatformProducerOrderHistoryItemFields
    }

    createdAt
    updatedAt
  }
`;

export const PLATFORM_MERCHANT_ORDER_WITH_PLATFORM_PRODUCER_ORDER = gql`
  ${PLATFORM_MERCHANT_ORDER_FIELDS}
  ${PLATFORM_PRODUCER_ORDER_FIELDS}
  fragment PlatformMerchantOrderWithPlatformProducerOrderFields on PlatformMerchantOrderWithPlatformProducerOrder {
    platformMerchantOrder {
      ...PlatformMerchantOrderFields
    }
    platformProducerOrder {
      ...PlatformProducerOrderFields
    }
  }
`;

//--------------------------------------------------------- Platform Producer Order Production Plan ---------------------------------------------------------

export const DESIGNER_ASSETS_FIELDS = gql`
  ${FONT_FIELDS}
  ${SYMBOL_FIELDS}
  ${SYMBOL_COLLECTION_FIELDS}
  ${PIECE_FIELDS}
  ${ACCESSORY_FIELDS}
  fragment DesignerAssetsFields on DesignerAssets {
    fonts {
      ...FontFields
    }
    symbols {
      ...SymbolFields
    }
    symbolCollections {
      ...SymbolCollectionFields
    }
    pieces {
      ...PieceFields
    }
    accessories {
      ...AccessoryFields
    }
  }
`;

export const PRODUCTION_ITEM_ACCESSORY_FIELDS = gql`
  ${ACCESSORY_REFERENCE_FIELDS}
  fragment ProductionItemAccessoryFields on ProductionItemAccessory {
    id
    quantity
    accessoryReference {
      ...AccessoryReferenceFields
    }
    inventoryReference
  }
`;

export const PRODUCTION_PLAN_SIDE_FIELDS = gql`
  ${MEDIA_FIELDS}
  ${IMAGE_FIELDS}
  fragment ProductionPlanPieceSideFields on ProductionPlanPieceSide {
    side
    image {
      ...ImageFields
    }
    vector {
      ...MediaFields
    }
  }
`;

export const PRODUCTION_ITEM_PIECE_FIELDS = gql`
  ${PIECE_REFERENCE_FIELDS}
  ${PRODUCTION_PLAN_SIDE_FIELDS}
  ${ENGRAVING_FIELDS}
  fragment ProductionItemPieceFields on ProductionItemPiece {
    id
    quantity
    pieceReference {
      ...PieceReferenceFields
    }
    inventoryReference
    pieceSides {
      ...ProductionPlanPieceSideFields
    }
    engravings {
      ...EngravingFields
    }
  }
`;

export const PRODUCTION_PLAN_FIELDS = gql`
  ${PRODUCTION_ITEM_PIECE_FIELDS}
  ${PRODUCTION_ITEM_ACCESSORY_FIELDS}
  ${DESIGNER_ASSETS_FIELDS}
  fragment ProductionPlanFields on ProductionPlan {
    id
    piecePlans {
      ...ProductionItemPieceFields
    }
    accessoryPlans {
      ...ProductionItemAccessoryFields
    }
    assets {
      ...DesignerAssetsFields
    }
  }
`;

export const PLATFORM_PRODUCER_ORDER_PPRODUCTION_PLAN_ITEM_FIELDS = gql`
  ${PRODUCTION_PLAN_FIELDS}
  fragment PlatformProducerOrderProductionPlanItemFields on PlatformProducerOrderProductionPlanItem {
    id
    platformProducerOrderItemId
    productionPlan {
      ...ProductionPlanFields
    }
  }
`;

export const PLATFORM_PRODUCER_ORDER_PPRODUCTION_PLAN_FIELDS = gql`
  ${PLATFORM_PRODUCER_ORDER_PPRODUCTION_PLAN_ITEM_FIELDS}
  fragment PlatformProducerOrderProductionPlanFields on PlatformProducerOrderProductionPlan {
    id
    platformProducerOrderId
    items {
      ...PlatformProducerOrderProductionPlanItemFields
    }
    createdAt
    updatedAt
  }
`;

export const PLATFORM_PRODUCER_ORDER_AND_PPRODUCTION_PLAN_FIELDS = gql`
  ${PLATFORM_PRODUCER_ORDER_FIELDS}
  ${PLATFORM_PRODUCER_ORDER_PPRODUCTION_PLAN_FIELDS}
  fragment PlatformProducerOrderAndProductionPlanFields on PlatformProducerOrderAndProductionPlan {
    platformProducerOrder {
      ...PlatformProducerOrderFields
    }
    platformProducerOrderProductionPlan {
      ...PlatformProducerOrderProductionPlanFields
    }
  }
`;
